/**
 * AdminMessageAnalytics Component - Analytics view for a message
 *
 * Features:
 * - Key metrics (impressions, dismissals, rates)
 * - Daily breakdown chart (simple bar visualization)
 */

import React, { useState, useEffect } from 'react';
import * as adminMessagesApi from '../../../api/adminMessages';
import './AdminMessageAnalytics.css';

function AdminMessageAnalytics({ message, onBack }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, [message.id]);

  const loadAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminMessagesApi.getAnalytics(message.id);
      setAnalytics(result.data);
    } catch (err) {
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const formatRate = (rate) => {
    if (rate === null || rate === undefined) return '-';
    return `${(rate * 100).toFixed(1)}%`;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="admin-message-analytics">
        <div className="admin-message-analytics__header">
          <button className="btn-ghost" onClick={onBack}>← Back</button>
          <h2>Analytics: {message.title}</h2>
        </div>
        <div className="admin-message-analytics__loading">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-message-analytics">
        <div className="admin-message-analytics__header">
          <button className="btn-ghost" onClick={onBack}>← Back</button>
          <h2>Analytics: {message.title}</h2>
        </div>
        <div className="admin-message-analytics__error">
          {error}
          <button onClick={loadAnalytics}>Retry</button>
        </div>
      </div>
    );
  }

  const maxDaily = analytics?.daily?.reduce((max, d) =>
    Math.max(max, d.impressions || 0), 0) || 1;

  return (
    <div className="admin-message-analytics">
      <div className="admin-message-analytics__header">
        <button className="btn-ghost" onClick={onBack}>← Back</button>
        <h2>Analytics: {message.title}</h2>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary">
        <div className="analytics-card">
          <div className="analytics-card__value">
            {formatNumber(analytics?.totals?.impressions)}
          </div>
          <div className="analytics-card__label">Total Impressions</div>
        </div>
        <div className="analytics-card">
          <div className="analytics-card__value">
            {formatNumber(analytics?.totals?.dismissals)}
          </div>
          <div className="analytics-card__label">Dismissals</div>
        </div>
        <div className="analytics-card">
          <div className="analytics-card__value">
            {formatNumber(analytics?.totals?.primary_clicks)}
          </div>
          <div className="analytics-card__label">Button Clicks</div>
        </div>
        <div className="analytics-card">
          <div className="analytics-card__value">
            {formatNumber(analytics?.totals?.dont_show_again)}
          </div>
          <div className="analytics-card__label">Don't Show Again</div>
        </div>
      </div>

      {/* Rates */}
      <div className="analytics-rates">
        <div className="analytics-rate">
          <span className="analytics-rate__label">Dismissal Rate:</span>
          <span className="analytics-rate__value">
            {formatRate(analytics?.rates?.dismissal_rate)}
          </span>
        </div>
        <div className="analytics-rate">
          <span className="analytics-rate__label">Click Rate:</span>
          <span className="analytics-rate__value">
            {formatRate(analytics?.rates?.click_rate)}
          </span>
        </div>
        <div className="analytics-rate">
          <span className="analytics-rate__label">Opt-out Rate:</span>
          <span className="analytics-rate__value">
            {formatRate(analytics?.rates?.opt_out_rate)}
          </span>
        </div>
      </div>

      {/* Daily Breakdown */}
      {analytics?.daily?.length > 0 && (
        <div className="analytics-daily">
          <h3>Daily Breakdown (Last 30 Days)</h3>
          <div className="analytics-chart">
            {analytics.daily.map((day, idx) => (
              <div key={idx} className="analytics-chart__bar-container">
                <div
                  className="analytics-chart__bar"
                  style={{ height: `${(day.impressions / maxDaily) * 100}%` }}
                  title={`${day.date}: ${day.impressions} impressions`}
                />
                <div className="analytics-chart__label">
                  {new Date(day.date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(!analytics?.daily || analytics.daily.length === 0) && (
        <div className="analytics-daily analytics-daily--empty">
          <h3>Daily Breakdown</h3>
          <p>No daily data available yet.</p>
        </div>
      )}
    </div>
  );
}

export default AdminMessageAnalytics;
